import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Badge } from '@/components/arc/badge/badge';
import { Breadcrumb } from '@/components/arc/breadcrumb/breadcrumb';
import { Button } from '@/components/arc/button/button';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { JsonViewer } from '@/components/arc/json-viewer/json-viewer';
import { Progress } from '@/components/arc/progress/progress';
import { ScrollArea } from '@/components/arc/scroll-area/scroll-area';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { Switch } from '@/components/arc/switch/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/arc/tabs/tabs';
import { AnalysisStatusBadge, PlatformBadge } from '@/components/status/StatusBadge';
import { MomentCard, Player, RiskBadge, Timeline, type PlayerHandle } from '@/components/video';
import { useVideo } from '@/api/hooks';
import { useJobForRef } from '@/api/ws';
import { errorMessage } from '@/api/client';
import type { MomentOut, VideoOut } from '@/api/types';
import { useTimezone } from '@/module/MomentProvider';
import { useHref } from '@/module/basePath';
import { fmtDate, fmtDuration, fmtMoney, fmtNumber, labelize } from '@/lib/format';
import { VideoPerformanceBlock } from '../segments/PerformanceCard';
import { AnalyzeDialog } from './AnalyzeDialog';
import styles from './VideoDetailPage.module.css';

export function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const href = useHref();
  const tz = useTimezone();
  const video = useVideo(id);
  const job = useJobForRef(id);
  const player = useRef<PlayerHandle>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [currentSec, setCurrentSec] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [follow, setFollow] = useState(true);
  const [tab, setTab] = useState('overview');
  const [analyzeTarget, setAnalyzeTarget] = useState<VideoOut | null>(null);

  const moments: MomentOut[] = useMemo(() => video.data?.moments ?? [], [video.data]);
  const duration = video.data?.duration_sec ?? moments.at(-1)?.end_sec ?? 0;
  const selectedIndex = moments.findIndex((m) => m.id === selectedId);
  const selected = selectedIndex >= 0 ? (moments[selectedIndex] ?? null) : null;

  // Keep the selected moment in step with playback while following.
  useEffect(() => {
    if (!follow || moments.length === 0) return;
    const cur = moments.find((m) => currentSec >= m.start_sec && currentSec < m.end_sec);
    if (cur && cur.id !== selectedId) setSelectedId(cur.id);
  }, [currentSec, follow, moments, selectedId]);

  // `?t=<seconds>` preselects the moment at that time and seeks once the player is likely ready.
  const tParam = sp.get('t');
  const seeked = useRef(false);
  useEffect(() => {
    if (seeked.current || !moments.length || tParam === null) return;
    const t = Number(tParam);
    if (Number.isNaN(t)) return;
    const m = moments.find((x) => t >= x.start_sec && t < x.end_sec) ?? moments[0];
    if (!m) return;
    setSelectedId(m.id);
    seeked.current = true;
    const timer = setTimeout(() => player.current?.seekTo(t), 1200);
    return () => clearTimeout(timer);
  }, [moments, tParam]);

  const seekTo = useCallback((sec: number) => {
    player.current?.seekTo(sec);
    setFollow(true);
  }, []);

  const select = useCallback(
    (m: MomentOut) => {
      setSelectedId(m.id);
      seekTo(m.start_sec);
    },
    [seekTo],
  );

  useEffect(() => {
    if (!selectedId || !listRef.current) return;
    listRef.current
      .querySelector<HTMLElement>(`[data-mid="${selectedId}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const videosHref = href('/videos');
  const crumbs = (title: string) => (
    <Breadcrumb
      items={[
        {
          label: 'Videos',
          href: videosHref,
          onClick: (e) => {
            e.preventDefault();
            navigate(videosHref);
          },
        },
        { label: title },
      ]}
    />
  );

  if (video.isError) {
    return (
      <main className="page">
        <PageHeading title="Video" above={crumbs('Video')} />
        <Alert tone="danger" title="Could not load this video">
          <span className={styles.alertBody}>
            {errorMessage(video.error)}
            <Button size="sm" variant="secondary" onClick={() => void video.refetch()}>
              Try again
            </Button>
          </span>
        </Alert>
      </main>
    );
  }

  if (!video.data) {
    return (
      <main className="page" aria-busy="true">
        <PageHeading title="Loading video" above={crumbs('Video')} />
        <div className={styles.layout}>
          <div className={styles.main}>
            <div className={styles.playerSkeleton}>
              <Skeleton lines={3} label="Loading player" />
            </div>
            <Skeleton lines={4} label="Loading moments" />
          </div>
          <Skeleton lines={8} label="Loading moment details" />
        </div>
      </main>
    );
  }

  const v = video.data;
  const title = v.title || v.external_id;
  const overallRisk = (v.overall_brand_safety as { risk?: string } | null)?.risk;
  const brandCount = new Set(moments.flatMap((m) => m.brands.map((b) => b.name))).size;
  const working = v.analysis_status === 'analyzing' || v.analysis_status === 'queued';
  const meta = [
    v.channel_name || labelize(v.platform),
    v.published_at ? fmtDate(v.published_at, tz) : null,
    fmtDuration(v.duration_sec),
  ]
    .filter(Boolean)
    .join(' · ');

  const emptyMomentsCopy =
    v.analysis_status === 'failed'
      ? 'The analysis failed. Run it again to get moments.'
      : v.analysis_status === 'skipped'
        ? `The analysis was skipped${v.skip_reason ? `: ${v.skip_reason}` : '.'}`
        : working
          ? 'Moments appear here once the analysis finishes.'
          : 'The analysis found no distinct moments in this video.';

  return (
    <main className="page">
      <PageHeading
        above={crumbs(title)}
        title={title}
        description={meta}
        actions={
          <>
            <a className={styles.extLink} href={v.url} target="_blank" rel="noreferrer">
              <ExternalLink size={16} strokeWidth={1.75} aria-hidden="true" />
              {v.platform === 'youtube' ? 'Open on YouTube' : 'Open source page'}
            </a>
            <Button onClick={() => setAnalyzeTarget(v)} disabled={v.analysis_status === 'analyzing'}>
              {v.analysis_status === 'done' ? 'Reanalyze video' : 'Analyze video'}
            </Button>
          </>
        }
        below={
          <div className="row-2">
            <AnalysisStatusBadge status={v.analysis_status} />
            <PlatformBadge platform={v.platform} />
            <Badge size="sm">{labelize(v.analysis_tier)} tier</Badge>
            {overallRisk && <RiskBadge risk={overallRisk} />}
          </div>
        }
      />

      {working && (
        <Alert
          tone="info"
          title={
            job?.message || (v.analysis_status === 'analyzing' ? 'Analyzing video' : 'Queued for analysis')
          }
        >
          {job ? (
            <Progress value={Math.round((job.progress ?? 0) * 100)} label="Analysis progress" showValue />
          ) : undefined}
        </Alert>
      )}
      {v.analysis_status === 'failed' && (
        <Alert tone="danger" title="Analysis failed">
          <span className={styles.alertBody}>
            {v.error || 'The analysis did not finish.'}
            <Button size="sm" variant="secondary" onClick={() => setAnalyzeTarget(v)}>
              Try again
            </Button>
          </span>
        </Alert>
      )}
      {v.analysis_status === 'skipped' && (
        <Alert tone="warning" title="Analysis skipped">
          {v.skip_reason || 'This video was skipped.'}
        </Alert>
      )}

      <div className={styles.layout}>
        <div className={styles.main}>
          <Player
            ref={player}
            platform={v.platform}
            externalId={v.external_id}
            url={v.url}
            thumbnail={v.thumbnail_url}
            title={title}
            onTime={setCurrentSec}
          />

          <section className={styles.panel} aria-labelledby="moments-heading">
            <div className={styles.panelHead}>
              <h2 id="moments-heading" className={styles.h2}>
                Moments <span className="text-muted">({fmtNumber(moments.length)})</span>
              </h2>
              <div className={styles.panelTools}>
                <span className={`${styles.clock} mono`} aria-live="off">
                  {fmtDuration(currentSec)} / {fmtDuration(duration)}
                </span>
                <Switch label="Follow playback" checked={follow} onCheckedChange={setFollow} />
              </div>
            </div>
            {moments.length ? (
              <Timeline
                moments={moments}
                duration={duration}
                currentSec={currentSec}
                selectedId={selectedId}
                onSelect={select}
              />
            ) : (
              <EmptyState title="No moments yet" description={emptyMomentsCopy} />
            )}
          </section>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="list">Moment list</TabsTrigger>
              <TabsTrigger value="raw">Raw analysis</TabsTrigger>
            </TabsList>
            <TabsContent value="overview">
              <div className={styles.panel}>
                <dl className={styles.meta}>
                  <div>
                    <dt>Model</dt>
                    <dd className="mono">{v.model_used ?? 'Not analyzed'}</dd>
                  </div>
                  <div>
                    <dt>Frame rate</dt>
                    <dd>{v.fps_used ? `${v.fps_used} fps` : 'Not analyzed'}</dd>
                  </div>
                  <div>
                    <dt>Tokens</dt>
                    <dd>
                      {v.token_input != null
                        ? `${fmtNumber(v.token_input)} in, ${fmtNumber(v.token_output)} out`
                        : 'Not analyzed'}
                    </dd>
                  </div>
                  <div>
                    <dt>Cost</dt>
                    <dd className="mono">{v.cost_usd != null ? fmtMoney(v.cost_usd) : 'Not analyzed'}</dd>
                  </div>
                  <div>
                    <dt>Language</dt>
                    <dd>{v.language ?? 'Unknown'}</dd>
                  </div>
                  <div>
                    <dt>Brands</dt>
                    <dd>{fmtNumber(brandCount)}</dd>
                  </div>
                  <div>
                    <dt>Analyzed</dt>
                    <dd>{v.analyzed_at ? fmtDate(v.analyzed_at, tz) : 'Not yet'}</dd>
                  </div>
                  <div>
                    <dt>Added</dt>
                    <dd>{fmtDate(v.created_at, tz)}</dd>
                  </div>
                </dl>
                {v.overall_summary && (
                  <div className={styles.block}>
                    <h3 className={styles.h3}>Summary</h3>
                    <p className={styles.prose}>{v.overall_summary}</p>
                  </div>
                )}
                {v.overall_iab_categories.length > 0 && (
                  <div className={styles.block}>
                    <h3 className={styles.h3}>IAB categories</h3>
                    <div className="row-2">
                      {v.overall_iab_categories.map((i) => (
                        <Badge key={i} size="sm" className="mono">
                          {i}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {v.description && (
                  <div className={styles.block}>
                    <h3 className={styles.h3}>Description</h3>
                    <p className={`${styles.prose} ${styles.description}`}>{v.description}</p>
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="list">
              <div className={styles.listPanel}>
                {moments.length === 0 ? (
                  <EmptyState title="No moments yet" description={emptyMomentsCopy} />
                ) : (
                  <ScrollArea maxHeight={440} label="Moment list" viewportRef={listRef}>
                    <div className="rows">
                      {moments.map((m, i) => (
                        <button
                          key={m.id}
                          type="button"
                          data-mid={m.id}
                          data-selected={m.id === selectedId || undefined}
                          aria-pressed={m.id === selectedId}
                          className={styles.momentRow}
                          onClick={() => select(m)}
                        >
                          <span className={`${styles.rowIndex} mono`}>{i + 1}</span>
                          <span className={styles.dot} data-sentiment={m.sentiment} aria-hidden="true" />
                          <span className="visually-hidden">{labelize(m.sentiment)} sentiment.</span>
                          <span className={`${styles.rowTime} mono`}>
                            {fmtDuration(m.start_sec)} to {fmtDuration(m.end_sec)}
                          </span>
                          <span className={styles.rowText}>{m.scene_summary}</span>
                          {m.brands.length > 0 && (
                            <span className={styles.rowBrands}>{m.brands.map((b) => b.name).join(', ')}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </TabsContent>
            <TabsContent value="raw">
              <JsonViewer
                data={v}
                rootName="video"
                defaultExpandDepth={1}
                maxHeight={520}
                label="Raw analysis"
              />
            </TabsContent>
          </Tabs>

          <VideoPerformanceBlock videoId={v.id} />
        </div>

        <aside className={styles.side} aria-label="Selected moment">
          <MomentCard
            moment={selected}
            index={selectedIndex >= 0 ? selectedIndex : undefined}
            count={selectedIndex >= 0 ? moments.length : undefined}
            onSeek={seekTo}
            className={styles.card}
          />
        </aside>
      </div>

      <AnalyzeDialog video={analyzeTarget} onClose={() => setAnalyzeTarget(null)} />
    </main>
  );
}
